import { supabase } from './supabase'

/**
 * QR Sipariş (30 Eki 2026, supabase/qr-siparis.sql): müşteri masadaki
 * karekoddan sipariş verir, garson çağırır, hesap ister.
 *
 * Müşteri tarafı oturumsuz; her şey masa anahtarını doğrulayan fonksiyonlardan
 * geçiyor. Garson tarafı table_requests'i canlı dinliyor ve isteği
 * table_request_handle ile ele alıyor.
 */

export type TableRequestKind = 'order' | 'waiter' | 'bill'
export type TableRequestStatus = 'pending' | 'seen' | 'accepted' | 'rejected' | 'cancelled'
export type PayMethod = 'cash' | 'card'

/** Müşterinin gönderdiği sepet satırı; ad ve fiyatı sunucu menüden alıyor. */
export interface QrCartItem {
  productId: string
  qty: number
  note?: string
}

export interface QrTableInfo {
  /** Masanın adı ("Masa 5"). */
  name: string
  /** Salonun adı, varsa. */
  hall: string | null
  /** Ürünlerin hazır not seçenekleri (ürün id -> ["acısız", ...]). */
  notes: Record<string, string[]>
}

export interface QrBill {
  lines: { name: string; qty: number; totalKurus: number }[]
  totalKurus: number
  prepaidKurus: number
}

export interface QrRequestState {
  id: string
  kind: TableRequestKind
  status: TableRequestStatus
  createdAt: number
  handledAt?: number
}

const ms = (v: unknown) => (typeof v === 'string' ? Date.parse(v) : undefined)

/**
 * Yalnız yerel geliştirmede: /m/<kurum>/demo veritabanına gitmeden çalışıyor,
 * SQL uygulanmadan ekran görülebilsin diye. Garson 6 sn sonra "görüyor",
 * sipariş 10 sn sonra onaylanıyor. Yayın derlemesinde import.meta.env.DEV
 * false, bu dal hiç yok.
 */
const demo = new Map<string, { id: string; kind: TableRequestKind; status: TableRequestStatus; at: number; pay?: string }>()
function demoCall(name: string, args: Record<string, unknown>): unknown {
  const now = Date.now()
  for (const r of demo.values()) {
    if (r.status !== 'pending') continue
    if (r.kind !== 'order' && now - r.at > 6000) r.status = 'seen'
    if (r.kind === 'order' && now - r.at > 10000) r.status = 'accepted'
  }
  switch (name) {
    case 'qr_table':
      return { name: 'Masa 5', hall: 'Bahçe', notes: {} }
    case 'qr_bill':
      return {
        lines: [
          { name: 'Mercimek Çorbası', qty: 2, totalKurus: 18000 },
          { name: 'Izgara Köfte', qty: 1, totalKurus: 32000 },
          { name: 'Çay', qty: 3, totalKurus: 9000 },
        ],
        totalKurus: 59000,
        prepaidKurus: 0,
      }
    case 'qr_request': {
      const kind = args.what as TableRequestKind
      const same = [...demo.values()].find((r) => r.kind === kind && kind !== 'order' && r.status === 'pending')
      if (same) return same.id
      const id = `demo-${now}-${Math.random().toString(36).slice(2)}`
      demo.set(id, { id, kind, status: 'pending', at: now })
      return id
    }
    case 'qr_status':
      return (args.ids as string[])
        .map((id) => demo.get(id))
        .filter(Boolean)
        .map((r) => ({
          id: r!.id,
          kind: r!.kind,
          status: r!.status,
          createdAt: new Date(r!.at).toISOString(),
          handledAt: r!.status === 'pending' ? null : new Date().toISOString(),
        }))
    case 'qr_cancel': {
      const r = demo.get(String(args.rid))
      if (r) r.status = 'cancelled'
      return true
    }
  }
  return null
}

async function call<T>(name: string, args: Record<string, unknown>): Promise<T> {
  if (import.meta.env.DEV && args.k === 'demo') {
    await new Promise((r) => setTimeout(r, 350))
    return demoCall(name, args) as T
  }
  if (!supabase) throw new Error('Bağlantı yok.')
  const { data, error } = await supabase.rpc(name, args)
  if (error) throw new Error(error.message)
  return data as T
}

/** Masa; anahtar geçersizse ya da modül kapalıysa null (menü yine açılır). */
export async function fetchQrTable(tenantId: string, token: string): Promise<QrTableInfo | null> {
  const data = await call<Record<string, unknown> | null>('qr_table', { t: tenantId, k: token })
  if (!data) return null
  const notes: Record<string, string[]> = {}
  for (const [id, list] of Object.entries((data.notes ?? {}) as Record<string, unknown>)) {
    if (Array.isArray(list)) notes[id] = list.map(String).filter(Boolean)
  }
  return {
    name: String(data.name ?? ''),
    hall: typeof data.hall === 'string' && data.hall.trim() ? data.hall : null,
    notes,
  }
}

export async function fetchQrBill(tenantId: string, token: string): Promise<QrBill> {
  const data = await call<Record<string, unknown> | null>('qr_bill', { t: tenantId, k: token })
  const raw = Array.isArray(data?.lines) ? (data!.lines as Record<string, unknown>[]) : []
  // Aynı ürün farklı turlarda ayrı satır; müşteriye tek satır.
  const merged = new Map<string, { name: string; qty: number; totalKurus: number }>()
  for (const l of raw) {
    const name = String(l.name ?? '')
    const cur = merged.get(name) ?? { name, qty: 0, totalKurus: 0 }
    cur.qty += Number(l.qty ?? 0)
    cur.totalKurus += Number(l.totalKurus ?? 0)
    merged.set(name, cur)
  }
  return {
    lines: [...merged.values()],
    totalKurus: Number(data?.totalKurus ?? 0),
    prepaidKurus: Number(data?.prepaidKurus ?? 0),
  }
}

export function sendQrRequest(
  tenantId: string,
  token: string,
  kind: TableRequestKind,
  opts: { items?: QrCartItem[]; pay?: PayMethod } = {},
): Promise<string> {
  return call<string>('qr_request', {
    t: tenantId,
    k: token,
    what: kind,
    items: opts.items ?? null,
    pay: opts.pay ?? null,
  })
}

export async function fetchQrStatus(tenantId: string, token: string, ids: string[]): Promise<QrRequestState[]> {
  if (ids.length === 0) return []
  const data = await call<Record<string, unknown>[] | null>('qr_status', { t: tenantId, k: token, ids })
  return (data ?? []).map((r) => ({
    id: String(r.id),
    kind: r.kind as TableRequestKind,
    status: r.status as TableRequestStatus,
    createdAt: ms(r.createdAt) ?? Date.now(),
    handledAt: ms(r.handledAt),
  }))
}

export const cancelQrRequest = (tenantId: string, token: string, id: string) =>
  call<boolean>('qr_cancel', { t: tenantId, k: token, rid: id })

// ---------------------------------------------------------------------------
// Garson tarafı

export interface TableRequestLine {
  productId: string
  name: string
  unitPriceKurus: number
  qty: number
  lineTotalKurus: number
  note?: string
}

export interface TableRequest {
  id: string
  tableId: string
  /** Masanın tam adı; adisyon bu adla tutuluyor. */
  tableLabel: string
  hallId?: string
  /** Salonuna bakan garson var mı; yoksa bütün garsonlar görür. */
  covered: boolean
  kind: TableRequestKind
  status: TableRequestStatus
  lines: TableRequestLine[]
  totalKurus: number
  payMethod?: PayMethod
  createdAt: number
}

type Row = Record<string, unknown>

function toRequest(r: Row): TableRequest {
  const lines = Array.isArray(r.lines) ? (r.lines as Row[]) : []
  return {
    id: String(r.id),
    tableId: String(r.table_id ?? ''),
    tableLabel: String(r.table_label ?? ''),
    hallId: typeof r.hall_id === 'string' && r.hall_id ? r.hall_id : undefined,
    covered: r.covered !== false,
    kind: r.kind as TableRequestKind,
    status: r.status as TableRequestStatus,
    lines: lines.map((l) => ({
      productId: String(l.productId ?? ''),
      name: String(l.name ?? ''),
      unitPriceKurus: Number(l.unitPriceKurus ?? 0),
      qty: Number(l.qty ?? 0),
      lineTotalKurus: Number(l.lineTotalKurus ?? 0),
      note: typeof l.note === 'string' && l.note ? l.note : undefined,
    })),
    totalKurus: Number(r.total_kurus ?? 0),
    payMethod: r.pay_method === 'cash' || r.pay_method === 'card' ? r.pay_method : undefined,
    createdAt: ms(r.created_at) ?? Date.now(),
  }
}

/**
 * Bekleyen istekler, canlı. Altı saatten eski bekleyen yok sayılıyor: kimsenin
 * bakmadığı dünkü çağrı bugün ekranı kaplamasın.
 */
export function subscribeTableRequests(tenantId: string, onData: (list: TableRequest[]) => void): () => void {
  if (import.meta.env.DEV && new URLSearchParams(window.location.search).has('qrdemo')) {
    return subscribeDemoRequests(onData)
  }
  if (!supabase) {
    onData([])
    return () => {}
  }
  const db = supabase
  let cancelled = false
  let timer: number | undefined

  const read = async () => {
    const since = new Date(Date.now() - 6 * 3600_000).toISOString()
    const { data, error } = await db
      .from('table_requests')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('status', 'pending')
      .gte('created_at', since)
      .order('created_at', { ascending: true })
    if (cancelled) return
    if (error) {
      console.error('[qr-siparis] okunamadı', error)
      return
    }
    onData((data ?? []).map((r) => toRequest(r as Row)))
  }
  const soon = () => {
    window.clearTimeout(timer)
    timer = window.setTimeout(() => void read(), 120)
  }

  void read()
  const channel = db
    .channel(`masa-istek:${tenantId}:${crypto.randomUUID()}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'table_requests', filter: `tenant_id=eq.${tenantId}` },
      soon,
    )
    .subscribe()
  // Bağlantı koptuysa bile bayatlamasın; ekrana dönülünce hemen.
  const poll = window.setInterval(() => void read(), 20_000)
  const onVisible = () => document.visibilityState === 'visible' && void read()
  document.addEventListener('visibilitychange', onVisible)

  return () => {
    cancelled = true
    window.clearTimeout(timer)
    window.clearInterval(poll)
    document.removeEventListener('visibilitychange', onVisible)
    void db.removeChannel(channel)
  }
}

/** true: bu cihaz aldı; false: başka garson daha önce ele almış. */
export const handleTableRequest = (id: string, status: 'seen' | 'accepted' | 'rejected') =>
  import.meta.env.DEV && id.startsWith('demo-')
    ? Promise.resolve(demoHandle(id))
    : call<boolean>('table_request_handle', { r: id, st: status })

/**
 * Yalnız yerel geliştirmede, adreste ?qrdemo varken: garson ekranı SQL
 * uygulanmadan görülebilsin. Üç istek hemen, dördüncüsü 15 sn sonra geliyor.
 */
let demoList: TableRequest[] = []
let demoTell: ((l: TableRequest[]) => void) | null = null
function demoHandle(id: string) {
  demoList = demoList.filter((r) => r.id !== id)
  demoTell?.([...demoList])
  return true
}
function subscribeDemoRequests(onData: (list: TableRequest[]) => void) {
  const mk = (n: number, kind: TableRequestKind, extra: Partial<TableRequest> = {}): TableRequest => ({
    id: `demo-${n}`,
    tableId: `t${n}`,
    tableLabel: 'Salon → Masa ' + n,
    hallId: 'orhanli-salon',
    covered: true,
    kind,
    status: 'pending',
    lines: [],
    totalKurus: 0,
    createdAt: Date.now() - n * 40_000,
    ...extra,
  })
  demoList = [
    mk(3, 'order', {
      lines: [
        { productId: 'x1', name: 'Mercimek Çorbası', unitPriceKurus: 9000, qty: 2, lineTotalKurus: 18000 },
        { productId: 'x2', name: 'Izgara Köfte', unitPriceKurus: 32000, qty: 1, lineTotalKurus: 32000, note: 'az pişmiş' },
        { productId: 'x3', name: 'Ayran', unitPriceKurus: 4000, qty: 2, lineTotalKurus: 8000 },
      ],
      totalKurus: 58000,
    }),
    mk(1, 'waiter'),
    mk(2, 'bill', { payMethod: 'card' }),
  ]
  demoTell = onData
  onData([...demoList])
  const later = window.setTimeout(() => {
    demoList = [...demoList, mk(4, 'waiter', { createdAt: Date.now() })]
    onData([...demoList])
  }, 15_000)
  return () => {
    window.clearTimeout(later)
    demoTell = null
  }
}

export const renewTableQrToken = (tableId: string) => call<string>('qr_token_renew', { tbl: tableId })

/** Masanın karekodunun açtığı adres. */
export const tableQrUrl = (tenantId: string, token: string) => `${window.location.origin}/m/${tenantId}/${token}`
